import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useI18n } from '../../i18n';

interface ShareButtonsProps {
  url: string;
  title: string;
  description?: string;
  className?: string;
}

export function ShareButtons({ url, title, description, className = '' }: ShareButtonsProps) {
  const { language } = useI18n();
  const [showCopied, setShowCopied] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const fullUrl = typeof window !== 'undefined' ? `${window.location.origin}${url}` : url;
  const encodedUrl = encodeURIComponent(fullUrl);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description || '');

  // Izračunaj poziciju dropdown-a kada se otvori
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 256; // w-64 = 16rem = 256px
      const dropdownHeight = 400; // približna visina
      
      // Proveri da li ima mesta desno, inače pomeri levo
      let left = rect.right - dropdownWidth;
      if (left < 8) left = 8;
      
      // Proveri da li ima mesta dole, inače prikaži iznad
      let top = rect.bottom + 8;
      if (top + dropdownHeight > window.innerHeight) {
        top = rect.top - dropdownHeight - 8;
        if (top < 8) top = 8;
      }
      
      setDropdownPosition({ top, left });
    }
  }, [isOpen]);

  const shareLinks = {
    // Copy Link (najbitnije)
    copyLink: async () => {
      try {
        await navigator.clipboard.writeText(fullUrl);
        setShowCopied(true);
        setTimeout(() => setShowCopied(false), 2000);
      } catch (err) {
        // Fallback za starije browsere
        const textArea = document.createElement('textarea');
        textArea.value = fullUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setShowCopied(true);
        setTimeout(() => setShowCopied(false), 2000);
      }
    },
    // WhatsApp
    whatsapp: () => {
      window.open(`https://wa.me/?text=${encodedTitle}%20${encodedUrl}`, '_blank');
    },
    // Viber
    viber: () => {
      window.open(`viber://forward?text=${encodedTitle}%20${encodedUrl}`, '_blank');
    },
    // Facebook
    facebook: () => {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedTitle}`, '_blank');
    },
    // Instagram (ne može direktan link, kopira u clipboard za paste)
    instagram: async () => {
      await navigator.clipboard.writeText(`${title}\n${fullUrl}`);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
      // Opcionalno otvaranje Instagrama
      window.open('https://www.instagram.com/', '_blank');
    },
    // Email
    email: () => {
      window.open(`mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`, '_blank');
    },
    // Telegram
    telegram: () => {
      window.open(`https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`, '_blank');
    },
  };

  return (
    <div className={`relative ${className}`} onClick={(e) => e.stopPropagation()}>
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="flex items-center gap-2 px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-amber-500 dark:hover:text-amber-400 transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
        </svg>
        <span className="text-sm font-medium">{language === 'sr' ? 'Podeli' : 'Share'}</span>
      </button>

      {/* Portal za dropdown - izlazi iz overflow kontejnera */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop */}
              <div 
                className="fixed inset-0 z-[9998]" 
                onClick={() => setIsOpen(false)}
              />
              
              {/* Dropdown - uvek fixed pozicija */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.15 }}
                style={{
                  position: 'fixed',
                  top: dropdownPosition.top,
                  left: dropdownPosition.left,
                }}
                className="w-64 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-[9999] max-h-[80vh] overflow-y-auto"
              >
                <div className="p-2">
                  <p className="px-3 py-2 text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {language === 'sr' ? 'Podeli putem' : 'Share via'}
                  </p>

                {/* Kopiraj link - NAJBITNIJI */}
                <button
                  onClick={() => {
                    shareLinks.copyLink();
                    setTimeout(() => setIsOpen(false), 1500);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center">
                    {showCopied ? (
                      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-slate-600 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {showCopied 
                        ? (language === 'sr' ? 'Kopirano!' : 'Copied!') 
                        : (language === 'sr' ? 'Kopiraj link' : 'Copy link')}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {language === 'sr' ? 'Podeli gde god želiš' : 'Share anywhere'}
                    </p>
                  </div>
                </button>

                <div className="h-px bg-slate-200 dark:bg-slate-700 my-2" />

                {/* WhatsApp */}
                <button
                  onClick={() => {
                    shareLinks.whatsapp();
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <span className="font-medium text-slate-900 dark:text-white">WhatsApp</span>
                </button>

                {/* Viber */}
                <button
                  onClick={() => {
                    shareLinks.viber();
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-[#7360F2] flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M11.398.002C9.474.028 5.036.244 2.945 2.09.96 3.84.208 6.604.032 9.89c-.173 3.283-.389 9.398 5.62 11.078h.005l-.004 2.535s-.038.996.638 1.2c.817.246 1.299-.522 2.08-1.36.428-.46.999-1.12 1.43-1.633 3.937.322 6.963-.416 7.312-.524.803-.25 5.342-.82 6.082-6.68.764-6.045-.356-9.863-2.358-11.59-.5-.439-.976-.794-1.372-1.078C17.461.654 14.143-.037 11.398.002zm.166 1.725c2.298-.014 5.247.48 6.903 1.746l.025.016c.349.25.755.543 1.19.926 1.629 1.403 2.454 4.69 1.81 9.71-.62 4.907-4.12 5.316-4.805 5.53-.29.09-2.88.722-6.213.524 0 0-2.461 2.922-3.231 3.696-.12.124-.272.181-.368.159-.135-.03-.172-.175-.171-.387l.02-4.008c-5.123-1.413-4.77-6.72-4.631-9.358.147-2.776.774-5.106 2.425-6.574 1.695-1.497 5.51-1.951 7.046-1.98zM8.344 5.918a.693.693 0 00-.528.26C7.544 6.5 6.983 7.18 6.928 8.128c-.054.947.258 2.18 1.096 3.558.846 1.37 2.398 3.425 5.227 4.465 1.145.42 2.044.515 2.736.43.693-.087 1.166-.345 1.572-.725a.693.693 0 00.13-.81l-.77-1.465a.693.693 0 00-.698-.37c-.323.046-.723.153-1.082.328-.36.175-.666.417-.808.617-.098.138-.253.218-.417.218a.697.697 0 01-.227-.038c-.748-.258-1.59-.771-2.319-1.402-.73-.63-1.358-1.387-1.716-2.093a.693.693 0 01.1-.814c.139-.152.32-.417.463-.748.143-.331.232-.716.257-1.018a.693.693 0 00-.356-.703l-1.43-.813a.693.693 0 00-.344-.086zm3.096.506a.347.347 0 01.35.35.347.347 0 01-.349.35c-.904-.007-1.645.307-2.235.917-.59.61-.902 1.383-.908 2.267a.347.347 0 01-.348.352.347.347 0 01-.351-.355c.008-1.069.388-1.997 1.1-2.73.712-.734 1.623-1.12 2.743-1.151zm-.175 1.214a.347.347 0 01.346.351.347.347 0 01-.35.345c-.519-.007-.952.16-1.308.515-.357.356-.549.802-.554 1.323a.347.347 0 01-.349.346.347.347 0 01-.345-.355c.006-.705.265-1.305.745-1.79.48-.484 1.078-.728 1.815-.735zm3.534.063a1.3 1.3 0 011.252 1.37c-.015.368-.17.706-.434.952a1.298 1.298 0 01-.977.367 1.3 1.3 0 01-1.252-1.37 1.3 1.3 0 011.411-1.32z"/>
                    </svg>
                  </div>
                  <span className="font-medium text-slate-900 dark:text-white">Viber</span>
                </button>

                {/* Facebook */}
                <button
                  onClick={() => {
                    shareLinks.facebook();
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-[#1877F2] flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </div>
                  <span className="font-medium text-slate-900 dark:text-white">Facebook</span>
                </button>

                {/* Instagram */}
                <button
                  onClick={() => {
                    shareLinks.instagram();
                    setTimeout(() => setIsOpen(false), 1500);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">Instagram</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {language === 'sr' ? 'Kopira link' : 'Copies link'}
                    </p>
                  </div>
                </button>

                {/* Telegram */}
                <button
                  onClick={() => {
                    shareLinks.telegram();
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-[#0088cc] flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M11.944 0A12 12 0 1 0 24 12.056A12.01 12.01 0 0 0 11.944 0Zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472c-.18 1.898-.962 6.502-1.36 8.627c-.168.9-.499 1.201-.82 1.23c-.696.065-1.225-.46-1.9-.902c-1.056-.693-1.653-1.124-2.678-1.8c-1.185-.78-.417-1.21.258-1.91c.177-.184 3.247-2.977 3.307-3.23c.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345c-.48.33-.913.49-1.302.48c-.428-.008-1.252-.241-1.865-.44c-.752-.245-1.349-.374-1.297-.789c.027-.216.325-.437.893-.663c3.498-1.524 5.83-2.529 6.998-3.014c3.332-1.386 4.025-1.627 4.476-1.635Z"/>
                    </svg>
                  </div>
                  <span className="font-medium text-slate-900 dark:text-white">Telegram</span>
                </button>

                {/* Email */}
                <button
                  onClick={() => {
                    shareLinks.email();
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-slate-500 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <span className="font-medium text-slate-900 dark:text-white">Email</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    )}
    </div>
  );
}
